/**
 * Graph mutation helpers for the journey builder.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file contains the core graph mutation logic for adding, removing, and
 * replacing nodes/edges in the journey DAG. It handles:
 *
 *   - Linear chain insertions and removals
 *   - Branching node (variant split / condition) creation and teardown
 *   - Branch-path step insertion and deletion
 *   - Placeholder replacement (both linear and branching)
 *   - Orphan cleanup when branches are removed
 *   - Join target protection and convergence node lifecycle
 *   - actionIndex bookkeeping for ACTION steps
 *
 * The complexity is inherent — these operations must maintain DAG invariants
 * (no cycles, correct out_step_id chains, edge/node consistency) across all
 * topology shapes. Mutations are expressed as React Flow NodeChange/EdgeChange
 * arrays so callers can apply them atomically.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Changing how nodes are wired into the graph
 *   - Fixing graph invariant bugs
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Adding a new send action type → update step-factory.ts and step-config.tsx
 *   - Adding a new branching step type → add a case to buildBranchingStepConfig (in this
 *     file) and update isBranchingStepType in @pushly/models; the mutation logic is generic
 *   - Changing step validation → update utils/graph/validation.ts or the relevant editor
 *   - Changing layout behavior → update elk-pipeline.ts or elk-config.ts
 */
import { DEFAULT_CUSTOM_EDGE_CONFIG, DEFAULT_BRANCH_EDGE_CONFIG, getEdgeId } from '../constants'
import { EdgeChange, NodeChange } from '@xyflow/react'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import {
    AddStepType,
    BranchEdgeData,
    LinearAddStepType,
    JourneyEdge,
    JourneyNode,
    JourneyNodeUIMeta,
    JourneyStepData,
    isVariantSplitStep,
    isConditionalStep,
    isActionStep,
} from '../types/journey-nodes'
import type { IActionStep, IConditionalStep, IDelayStep, IJourneyStep, IPlaceholderStep, IVariantSplitStep } from '@pushly/models/lib/structs/journeys'
import { updateStepConfig, updateStepMeta } from '@pushly/models/lib/structs/journeys'
import { CopyTypes } from '../types/journey-context'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { autoDistribute, getExclusivelyReachableSteps, buildNodeMap, isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { isBranchingStepType, getBranchingData } from '@pushly/models/lib/structs/journeys'
import type { IVariantBranch, IConditionBranch } from '@pushly/models/lib/structs/journeys'
import { ScopeCache, enclosingBranchingNodes } from '../utils/graph/scope'
import { checkScopeNesting, checkJoinDominance, MutationError } from '../utils/graph/invariants'
import { cloneNodeWithStep } from '../reducer'
import { simpleNotification } from '../../../_utils/utils'
import { isEmptyPlaceholder, graphHasMeaningfulSteps } from '../types/journey-nodes'
import { getBaseConfigurationForStepType } from './step-factory'

// ---- Shared helpers for branching node mutations ----

/**
 * Updates a branching node's specific branch out_step_id in the authoritative storage.
 */
function updateBranchOutStepId(
    branchingNode: JourneyNode,
    branchKey: string,
    newOutStepId: string | number | null,
): JourneyNode {
    const step = branchingNode.data.step
    if (!isVariantSplitStep(step) && !isConditionalStep(step)) return branchingNode

    const branches = step.configuration.branches.map((b) =>
        b.key === branchKey ? { ...b, out_step_id: newOutStepId } : b,
    )
    const clonedStep = updateStepConfig(step, { branches } as Partial<typeof step.configuration>)
    return cloneNodeWithStep(branchingNode, clonedStep)
}

/** Shape returned by buildBranchingStepConfig — normalized across VARIANT_SPLIT and CONDITION */
type BranchingStepConfig = {
    step: IVariantSplitStep | IConditionalStep
    nodeType: 'split-step' | 'condition-step'
    branches: Array<{ kind: string; key: string; label: string; out_step_id: string | number; percentage?: number }>
}

/**
 * Builds the step config and normalized branch list for a new branching node.
 * Centralizes the VARIANT_SPLIT vs CONDITION divergence into one place.
 */
function buildBranchingStepConfig(
    stepType: StepType.VARIANT_SPLIT | StepType.CONDITION,
    nodeId: string,
    convergenceId: string,
): BranchingStepConfig {
    if (stepType === StepType.VARIANT_SPLIT) {
        const percentages = autoDistribute(2)
        const branches: IVariantBranch[] = [
            { kind: 'variant', key: getRandId(), label: 'Variant A', percentage: percentages[0], out_step_id: convergenceId },
            { kind: 'variant', key: getRandId(), label: 'Variant B', percentage: percentages[1], out_step_id: convergenceId },
        ]

        return {
            step: {
                id: nodeId,
                type: StepType.VARIANT_SPLIT,
                configuration: { join_step_id: convergenceId, branches },
                meta: {},
            },
            nodeType: 'split-step',
            branches: branches.map((b) => ({
                kind: b.kind,
                key: b.key,
                label: b.label,
                out_step_id: convergenceId,
                percentage: b.percentage,
            })),
        }
    }

    // StepType.CONDITION
    const branches: [IConditionBranch, IConditionBranch] = [
        { kind: 'condition', key: 'yes', label: 'Yes', out_step_id: convergenceId },
        { kind: 'condition', key: 'no', label: 'No', out_step_id: convergenceId },
    ]

    return {
        step: {
            id: nodeId,
            type: StepType.CONDITION,
            configuration: {
                join_step_id: convergenceId,
                branches,
                condition: { field: '', operator: '', value: null },
            },
            meta: {},
        },
        nodeType: 'condition-step',
        branches: branches.map((b) => ({ kind: b.kind, key: b.key, label: b.label, out_step_id: convergenceId })),
    }
}

/**
 * Creates a placeholder convergence node with standard metadata.
 */
function createPlaceholderConvergenceNode(
    placeholderId: string,
    ownerNodeId: string,
    outStepId: string | number,
    position: { x: number; y: number },
): JourneyNode {
    const placeholderStep: IPlaceholderStep = {
        id: placeholderId,
        type: StepType.PLACEHOLDER,
        configuration: { out_step_id: outStepId },
        meta: {},
    }
    return {
        id: placeholderId,
        data: new JourneyStepData({
            step: placeholderStep,
            uiMeta: {
                placeholderRole: 'convergence',
                convergenceOf: ownerNodeId,
                joinTargetOf: [ownerNodeId],
            },
        }),
        position,
        type: 'custom',
    }
}

/**
 * Creates split edges for all branches of a branching node targeting a convergence node.
 */
function createBranchEdges(
    branchingNodeId: string,
    branches: BranchingStepConfig['branches'],
): EdgeChange<JourneyEdge>[] {
    return branches.map((branch) => {
        const edgeData: BranchEdgeData = {
            branchKey: branch.key,
            branchLabel: branch.label,
            ...(branch.percentage != null ? { percentage: branch.percentage } : {}),
        }
        return {
            type: 'add' as const,
            item: {
                ...DEFAULT_BRANCH_EDGE_CONFIG,
                id: `${getEdgeId(branchingNodeId)}-${branch.key}`,
                source: branchingNodeId,
                target: String(branch.out_step_id),
                sourceHandle: `branch-${branch.key}`,
                data: edgeData,
            },
        }
    })
}

export const getAddNodeMutations = (
    nodeType: Exclude<AddStepType, 'PASTE_COPIED_NODE'>,
    nodes: JourneyNode[],
    sourceNode: JourneyNode,
    targetNode: JourneyNode,
    overrideData?: IActionStep | IDelayStep,
    overrideType?: CopyTypes,
    branchKey?: string,
    edges: JourneyEdge[] = [],
): [
    nodeChanges: NodeChange<JourneyNode>[],
    edgeChanges: EdgeChange<JourneyEdge>[],
    newNode: JourneyNode | undefined,
] => {
    const nodeChanges: NodeChange<JourneyNode>[] = []
    const edgeChanges: EdgeChange<JourneyEdge>[] = []

    const sourceNodeId = sourceNode.data.id
    const targetNodeId = targetNode.data.id

    const newNodeId = getRandId()

    // adding a step after exit not supported
    if (sourceNode.data.step.type === StepType.EXIT) {
        return [nodeChanges, edgeChanges, undefined]
    }

    // --- Empty placeholder interception ---
    // If either endpoint is an empty placeholder and the graph has no meaningful steps,
    // replace the placeholder in-place instead of inserting a new node.
    const emptyPlaceholder = isEmptyPlaceholder(sourceNode)
        ? sourceNode
        : isEmptyPlaceholder(targetNode)
            ? targetNode
            : null

    if (emptyPlaceholder && !graphHasMeaningfulSteps(nodes)) {
        const [nc, ec, newConvergence] = replacePlaceholderWithStep(emptyPlaceholder, nodeType as AddStepType, nodes, edges)
        return [nc, ec, newConvergence ?? undefined]
    }

    // --- Invariant checks for nested branching insertions ---
    const isBranchingInsertion = isBranchingStepType(nodeType)
    if (isBranchingInsertion) {
        const scopeCache = new ScopeCache()
        const nodeMap = new Map(nodes.map((n) => [n.id, n]))
        const parentBranchingNodes = enclosingBranchingNodes(sourceNode.id, nodes, scopeCache)

        if (parentBranchingNodes.length > 0) {
            const joinStepId = targetNodeId.toString()

            const nestingError = checkScopeNesting(joinStepId, parentBranchingNodes, nodeMap, scopeCache)
            if (nestingError) {
                simpleNotification('warning', nestingError.message)
                return [[], [], undefined]
            }

            const dominanceError = checkJoinDominance(newNodeId, joinStepId, sourceNode.id, parentBranchingNodes, nodeMap, scopeCache)
            if (dominanceError) {
                simpleNotification('warning', dominanceError.message)
                return [[], [], undefined]
            }
        }
    }

    // --- Branching node creation (VARIANT_SPLIT or CONDITION) ---
    // When branchKey is provided, the source is a parent branching node and we're
    // inserting into one of its branch paths. We need to:
    //   1. Update the parent's branch out_step_id (not the top-level out_step_id)
    //   2. Replace the parent's branch edge (not the regular source edge)
    //   3. The new branching node's join_step_id = targetNodeId (the old branch target)
    // When branchKey is NOT provided, we're inserting on a regular edge.
    if (isBranchingStepType(nodeType)) {
        const isInsertingIntoBranch = branchKey && isBranchingNode(sourceNode)

        // Create convergence node between branching node and original target
        const convergenceId = getRandId()
        const convergenceNode = createPlaceholderConvergenceNode(
            convergenceId, newNodeId, targetNodeId,
            { x: targetNode.position.x, y: targetNode.position.y },
        )

        // Build the branching step config (handles VARIANT_SPLIT vs CONDITION differences)
        const branchingConfig = buildBranchingStepConfig(nodeType as StepType.VARIANT_SPLIT | StepType.CONDITION, newNodeId, convergenceId)

        const data = new JourneyStepData({
            step: branchingConfig.step,
            uiMeta: { source: 'add' },
        })

        const newNode: JourneyNode = {
            id: newNodeId,
            data,
            position: { x: 0, y: 0 },
            type: branchingConfig.nodeType,
        }

        if (isInsertingIntoBranch) {
            // Update the parent branching node's specific branch out_step_id
            const clonedSourceNode = updateBranchOutStepId(sourceNode, branchKey!, newNodeId)

            nodeChanges.push(
                { type: 'add', item: newNode },
                { type: 'add', item: convergenceNode },
                { type: 'replace', id: sourceNodeId.toString(), item: clonedSourceNode },
            )

            // Remove old branch edge: parent → old target
            const oldBranchEdgeId = `${getEdgeId(sourceNodeId)}-${branchKey}`
            edgeChanges.push({ type: 'remove', id: oldBranchEdgeId })

            // Add new branch edge: parent → new branching step (preserving branch data)
            const parentBranchingData = getBranchingNodeData(clonedSourceNode)
            const parentBranch = parentBranchingData?.branches.find((b) => b.key === branchKey)
            const parentEdgeData: BranchEdgeData = {
                branchKey: branchKey!,
                branchLabel: parentBranch?.label ?? branchKey!,
            }
            if (parentBranch && parentBranch.kind === 'variant') {
                parentEdgeData.percentage = parentBranch.percentage
            }
            edgeChanges.push({
                type: 'add',
                item: {
                    ...DEFAULT_BRANCH_EDGE_CONFIG,
                    id: oldBranchEdgeId,
                    source: sourceNodeId.toString(),
                    target: newNodeId.toString(),
                    sourceHandle: `branch-${branchKey}`,
                    data: parentEdgeData,
                },
            })
        } else {
            // Regular edge insertion (not inside a branch)
            const clonedStep = updateStepConfig(sourceNode.data.step, { out_step_id: newNodeId })
            const clonedSourceNode = cloneNodeWithStep(sourceNode, clonedStep)

            nodeChanges.push(
                { type: 'add', item: newNode },
                { type: 'add', item: convergenceNode },
                { type: 'replace', id: sourceNodeId.toString(), item: clonedSourceNode },
            )

            // Remove old edge from source → target
            edgeChanges.push({
                type: 'remove',
                id: getEdgeId(sourceNodeId),
            })

            // Add edge from source → branching step (normal edge)
            edgeChanges.push({
                type: 'add',
                item: {
                    ...DEFAULT_CUSTOM_EDGE_CONFIG,
                    id: getEdgeId(sourceNodeId),
                    source: sourceNodeId.toString(),
                    target: newNodeId.toString(),
                },
            })
        }

        // Add edge: convergence → original target (standard edge)
        edgeChanges.push({
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(convergenceId),
                source: convergenceId,
                target: targetNodeId.toString(),
            },
        })

        // Add one split edge per branch targeting convergence node
        edgeChanges.push(...createBranchEdges(newNodeId, branchingConfig.branches))

        return [nodeChanges, edgeChanges, newNode]
    }

    // --- Inserting a step into a branch path ---
    // When branchKey is provided, the source is a branching node. We need to update
    // the branch's out_step_id and replace the branch edge.
    if (branchKey && isBranchingNode(sourceNode)) {
        const branchingData = getBranchingNodeData(sourceNode)
        const branch = branchingData?.branches.find((b) => b.key === branchKey)
        if (!branch) {
            return [nodeChanges, edgeChanges, undefined]
        }

        // Update the branch's out_step_id to point to the new node
        // We need to update the authoritative storage directly
        const clonedSourceNode = updateBranchOutStepId(sourceNode, branchKey!, newNodeId)

        const meta: Record<string, any> = {}

        const data = new JourneyStepData({
            step: getBaseConfigurationForStepType(nodeType, newNodeId, targetNodeId, { overrideData, overrideType, meta }),
            uiMeta: { source: overrideType ? overrideType : 'add' },
        })

        const newNode: JourneyNode = {
            id: newNodeId,
            data,
            position: { x: 0, y: 0 },
            type: 'custom',
        }

        nodeChanges.push(
            { type: 'add', item: newNode },
            { type: 'replace', id: sourceNodeId.toString(), item: clonedSourceNode },
        )

        // Remove old branch edge: branching node → old target
        const oldBranchEdgeId = `${getEdgeId(sourceNodeId)}-${branchKey}`
        edgeChanges.push({ type: 'remove', id: oldBranchEdgeId })

        // Build BranchEdgeData for the replacement edge
        const edgeData: BranchEdgeData = {
            branchKey: branch.key,
            branchLabel: branch.label,
        }
        if (branch.kind === 'variant') {
            edgeData.percentage = branch.percentage
        }

        // Add new branch edge: branching node → new node
        edgeChanges.push({
            type: 'add',
            item: {
                ...DEFAULT_BRANCH_EDGE_CONFIG,
                id: `${getEdgeId(sourceNodeId)}-${branchKey}`,
                source: sourceNodeId.toString(),
                target: newNodeId.toString(),
                sourceHandle: `branch-${branchKey}`,
                data: edgeData,
            },
        })

        // Add regular edge: new node → old target
        edgeChanges.push({
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(newNodeId),
                source: newNodeId,
                target: targetNodeId.toString(),
            },
        })

        return [nodeChanges, edgeChanges, newNode]
    }

    // re-assign out_step_id to new node - added to changes via below type: "replace" change
    const clonedStep = updateStepConfig(sourceNode.data.step, { out_step_id: newNodeId })
    const clonedSourceNode = cloneNodeWithStep(sourceNode, clonedStep)
    const meta: Record<string, any> = {}

    // if an ACTION step is added determine its step index
    // (All JourneyActionType values produce StepType.ACTION steps — actionIndex is used by
    // catalog-item-pick-strategy to determine prior send count. Kept consistent with
    // serialization.ts which assigns actionIndex to every ACTION step.)
    let newActionIndex
    if (nodeType !== StepType.DELAY) {
        const startingIndex = nodes.findIndex((n) => n.data.step.id === sourceNodeId)

        if (startingIndex > 0) {
            // Scan backwards from the source to find the most recent ACTION and set index + 1
            for (let i = startingIndex; i >= 0; i--) {
                if (nodes[i].data.step.type === StepType.ACTION) {
                    if (isActionStep(nodes[i].data.step)) {
                        const actionStep = nodes[i].data.step
                        const prevIdx = (actionStep.meta?.actionIndex as number | undefined) ?? -1
                        newActionIndex = prevIdx + 1
                    }
                    break
                }
            }
        } else {
            newActionIndex = 0
        }

        meta.actionIndex = newActionIndex
    }

    const data = new JourneyStepData({
        step: getBaseConfigurationForStepType(nodeType, newNodeId, targetNodeId, { overrideData, overrideType, meta }),
        uiMeta: { source: overrideType ? overrideType : 'add' },
    })

    // Ensure the computed actionIndex is actually set on the new Action step
    if (nodeType !== StepType.DELAY) {
        data.step.meta = {
            ...(data.step.meta ?? {}),
            actionIndex: newActionIndex ?? 0,
        }
    }

    const newNode: JourneyNode = {
        id: newNodeId,
        data,
        position: {
            x: 0,
            y: 0, // auto layout called in base builder should layout position
        },
        type: 'custom',
    }

    // update nodes from source to new and new to target
    // current: source { out_step_id: target }
    // new: source {out_step_id: new }, new {out_step_id: target}
    nodeChanges.push(
        // mutation to add newly created step/node
        {
            type: 'add',
            item: newNode,
        },
        // replace source node with out_step_id: newNodeId
        {
            type: 'replace',
            id: sourceNodeId.toString(),
            item: clonedSourceNode,
        },
    )

    /**
     * update edges
     * ```
     *  _________________________________________________________
     * |                                                        |
     * |    current                                             |
     * |        nodes: [source] -----> [target]                 |
     * |        edges:      \__eid-source__/                    |
     * |--------------------------------------------------------|
     * |                                                        |
     * |    new                                                 |
     * |        nodes: [source]----> [new node] ----> [target]  |
     * |        edges:     \_eid-source_/ \_eid-new-node_/      |
     * |________________________________________________________|
     *
     * ```
     */
    edgeChanges.push(
        // add edge from source->new node
        {
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(sourceNodeId),
                source: sourceNodeId.toString(),
                target: newNodeId.toString(),
            },
        },
        // add edge from new node->target
        {
            type: 'add',
            item: {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(newNodeId),
                source: newNodeId,
                target: targetNodeId.toString(),
            },
        },
        // remove old edge from source->target
        {
            type: 'remove',
            id: getEdgeId(sourceNodeId),
        },
    )

    // apply position/meta changes for each node occurring after the added node
    nodeChanges.push(...applyChangesPostNode('add', nodes, targetNodeId.toString(), newNode.data.step.type))

    return [nodeChanges, edgeChanges, newNode]
}

export const getRemoveNodeMutations = (
    node: JourneyNode,
    nodes: JourneyNode[],
    edges: JourneyEdge[],
): [nodeChanges: NodeChange<JourneyNode>[], edgeChanges: EdgeChange<JourneyEdge>[], error?: MutationError] => {
    const nodeChanges: NodeChange<JourneyNode>[] = []
    const edgeChanges: EdgeChange<JourneyEdge>[] = []

    // cannot remove Trigger or Exit steps return empty changes
    if (node.data.step.type === StepType.EXIT || node.data.step.type === StepType.TRIGGER) {
        return [nodeChanges, edgeChanges]
    }

    // Block DIRECT deletion of placeholder nodes (both empty and convergence roles).
    // Cascade-delete from branching node removal does NOT hit this guard —
    // it collects owned convergence placeholders via stepsToRemove directly.
    if (node.data.step.type === StepType.PLACEHOLDER) {
        const role = node.data.uiMeta?.placeholderRole
        return [[], [], {
            invariantId: 'PLACEHOLDER_DELETE_BLOCKED',
            message: role === 'empty'
                ? 'Replace this placeholder by selecting a step type'
                : 'Delete the owning branching step to remove this convergence point',
            offendingNodeIds: [node.id],
        }]
    }

    // --- Join target protection ---
    // If this node is the join target of any branching node, revert it back to a convergence node
    // instead of deleting it. This handles the case where a user replaced a convergence node
    // with a real step and now wants to undo that replacement.
    const joinTargetOf: string[] | undefined = Array.isArray(node.data.uiMeta?.joinTargetOf)
        ? node.data.uiMeta.joinTargetOf
        : (typeof node.data.uiMeta?.joinTargetOf === 'string' && node.data.uiMeta.joinTargetOf)
            ? [node.data.uiMeta.joinTargetOf]
            : undefined

    if (joinTargetOf && joinTargetOf.length > 0) {
        const outStepId = node.data.step.configuration.out_step_id ?? null
        const convergenceStep: IPlaceholderStep = {
            id: node.data.step.id,
            type: StepType.PLACEHOLDER,
            configuration: { out_step_id: outStepId },
            meta: {},
        }

        const newData = new JourneyStepData({
            step: convergenceStep,
            uiMeta: {
                ...node.data.uiMeta,
                placeholderRole: 'convergence',
                convergenceOf: joinTargetOf[0],
            },
        })
        const newNode = { ...node, data: newData, type: 'custom' as const }

        return [
            [{ type: 'replace', id: node.id, item: newNode }],
            edgeChanges,
        ]
    }

    // --- Branching node removal (variant split or condition) ---
    if (isBranchingNode(node)) {
        const branchingData = getBranchingNodeData(node)
        if (!branchingData) return [nodeChanges, edgeChanges]

        const joinStepId = branchingData.join_step_id

        // Find the predecessor step (the node whose out_step_id points to this branching node)
        const predecessorEdge = edges.find((e) => e.target === node.id)!
        const predecessorNode = nodes.find((n) => n.id === predecessorEdge.source)!

        // Remove all edges connected to the branching node (incoming edge + all branch edges)
        const branchingEdges = edges.filter((e) => e.source === node.id || e.target === node.id)
        for (const edge of branchingEdges) {
            edgeChanges.push({ id: edge.id, type: 'remove' })
        }

        // Collect all steps exclusively in branch paths to remove them
        const allBranchOutStepIds = branchingData.branches.map((b) => b.out_step_id)
        const nodeMap = buildNodeMap(nodes)
        const stepsToRemove = new Set<string>()
        for (const branch of branchingData.branches) {
            const exclusive = getExclusivelyReachableSteps(
                branch.out_step_id,
                allBranchOutStepIds,
                joinStepId,
                nodes,
                nodeMap,
            )
            for (const stepId of exclusive) {
                stepsToRemove.add(stepId)
            }
        }

        // --- Auto-delete owned convergence node ---
        const joinNode = joinStepId != null ? nodes.find((n) => n.id === String(joinStepId)) : undefined
        let effectiveJoinStepId = joinStepId

        const isOwnedConvergence =
            joinNode &&
            joinNode.data.step.type === StepType.PLACEHOLDER &&
            joinNode.data.uiMeta?.convergenceOf === node.id

        if (isOwnedConvergence) {
            const joinTargetOfArr: string[] = Array.isArray(joinNode.data.uiMeta?.joinTargetOf)
                ? joinNode.data.uiMeta.joinTargetOf
                : []

            if (joinTargetOfArr.length === 1 && joinTargetOfArr[0] === node.id) {
                // Safe to auto-delete: owned, unreplaced, unshared
                const downstreamTargetId = joinNode.data.step.configuration.out_step_id ?? null
                stepsToRemove.add(joinNode.id)
                effectiveJoinStepId = downstreamTargetId
            } else {
                // Invariant violation: convergence node has unexpected joinTargetOf
                if (process.env.NODE_ENV === 'development') {
                    console.warn(
                        `Convergence node ${joinNode.id} has unexpected joinTargetOf:`,
                        joinTargetOfArr,
                        '— preserving node',
                    )
                }
            }
        }

        // Update joinTargetOf on the effective join target node: filter out the removed node's ID
        const effectiveJoinNode = effectiveJoinStepId != null
            ? nodes.find((n) => n.id === String(effectiveJoinStepId))
            : undefined

        // If we're NOT auto-deleting the convergence node, clean up its joinTargetOf
        let clonedJoinNode = joinNode
        if (joinNode && !stepsToRemove.has(joinNode.id)) {
            const currentJoinTargetOf: string[] = Array.isArray(joinNode.data.uiMeta?.joinTargetOf)
                ? joinNode.data.uiMeta.joinTargetOf
                : (joinNode.data.uiMeta?.joinTargetOf ? [joinNode.data.uiMeta.joinTargetOf] : [])
            const filtered = currentJoinTargetOf.filter((id: string) => id !== node.id)
            let newUIMeta: JourneyNodeUIMeta
            if (filtered.length === 0) {
                const { joinTargetOf: _, ...restMeta } = joinNode.data.uiMeta ?? {}
                newUIMeta = restMeta
            } else {
                newUIMeta = { ...joinNode.data.uiMeta, joinTargetOf: filtered }
            }
            const newData = new JourneyStepData({
                step: joinNode.data.step,
                uiMeta: newUIMeta,
            })
            clonedJoinNode = { ...joinNode, data: newData }
        }

        // Reconnect predecessor directly to effective join target
        // Check if predecessor is a branching node — if so, update the branch's out_step_id
        const predIncomingEdge = edges.find((e) => e.target === node.id && e.source === predecessorNode.id)
        const predBranchKey = predIncomingEdge?.data?.branchKey
        let clonedPredecessorNode: JourneyNode
        if (predBranchKey && isBranchingNode(predecessorNode)) {
            clonedPredecessorNode = updateBranchOutStepId(predecessorNode, predBranchKey, effectiveJoinStepId)
        } else {
            const clonedPredStep = updateStepConfig(predecessorNode.data.step, { out_step_id: effectiveJoinStepId })
            clonedPredecessorNode = cloneNodeWithStep(predecessorNode, clonedPredStep)
        }

        // Remove the branching node
        nodeChanges.push(
            { id: predecessorNode.id, type: 'replace', item: clonedPredecessorNode },
            { id: node.id, type: 'remove' },
        )
        // Replace the effective join node (for metadata updates) if it wasn't auto-deleted
        if (effectiveJoinNode && !stepsToRemove.has(effectiveJoinNode.id)) {
            // Use clonedJoinNode if the effective join node is the same as the original join node
            const effectiveItem = (joinNode && effectiveJoinNode.id === joinNode.id) ? clonedJoinNode! : effectiveJoinNode
            nodeChanges.push({ id: effectiveJoinNode.id, type: 'replace', item: effectiveItem })
        }
        // If the original join node is preserved (not auto-deleted) and differs from effective, replace it too
        if (joinNode && joinNode !== effectiveJoinNode && !stepsToRemove.has(joinNode.id)) {
            nodeChanges.push({ id: joinNode.id, type: 'replace', item: clonedJoinNode! })
        }

        // Remove exclusively reachable branch path steps and their edges
        // (this now also includes the convergence node when auto-deleted)
        for (const stepId of stepsToRemove) {
            nodeChanges.push({ id: stepId, type: 'remove' })
            const stepEdges = edges.filter((e) => e.source === stepId || e.target === stepId)
            for (const edge of stepEdges) {
                edgeChanges.push({ id: edge.id, type: 'remove' })
            }
        }

        // Add a single edge from predecessor to effective join target
        if (effectiveJoinStepId != null) {
            // If predecessor is a branching node, create a split edge; otherwise a regular edge
            if (predBranchKey && isBranchingNode(predecessorNode)) {
                const predBranchingData = getBranchingNodeData(predecessorNode)
                const predBranch = predBranchingData?.branches.find((b) => b.key === predBranchKey)
                const edgeData: BranchEdgeData = {
                    branchKey: predBranchKey,
                    branchLabel: predBranch?.label ?? predBranchKey,
                }
                if (predBranch && predBranch.kind === 'variant') {
                    edgeData.percentage = predBranch.percentage
                }
                edgeChanges.push({
                    type: 'add',
                    item: {
                        ...DEFAULT_BRANCH_EDGE_CONFIG,
                        id: `${getEdgeId(predecessorNode.data.id)}-${predBranchKey}`,
                        source: predecessorNode.id,
                        target: String(effectiveJoinStepId),
                        sourceHandle: `branch-${predBranchKey}`,
                        data: edgeData,
                    },
                })
            } else {
                edgeChanges.push({
                    type: 'add',
                    item: {
                        ...DEFAULT_CUSTOM_EDGE_CONFIG,
                        id: getEdgeId(predecessorNode.data.id),
                        source: predecessorNode.id,
                        target: String(effectiveJoinStepId),
                    },
                })
            }
        }

        return [nodeChanges, edgeChanges]
    }

    // --- Branch path step deletion ---
    // Check if the incoming edge is a branch-edge — if so, this node is the first step
    // in a branch path and we need to update the owning branching node's branch config.
    const incomingBranchEdge = edges.find((e) => e.target === node.id && e.type === 'branch-edge')
    if (incomingBranchEdge) {
        const owningBranchingNode = nodes.find((n) => n.id === incomingBranchEdge.source)
        if (owningBranchingNode && isBranchingNode(owningBranchingNode)) {
            const targetNodeEdge = edges.find((e) => e.source === node.id)!
            const postNode = nodes.find((n) => n.id === targetNodeEdge.target)!
            const edgeBranchKey = incomingBranchEdge.data?.branchKey

            if (edgeBranchKey) {
                // Update the branch's out_step_id in the authoritative storage
                const clonedOwningNode = updateBranchOutStepId(owningBranchingNode, edgeBranchKey, postNode.data.id)

                edgeChanges.push({
                    id: incomingBranchEdge.id,
                    type: 'replace',
                    item: {
                        ...incomingBranchEdge,
                        target: postNode.id,
                    },
                })

                nodeChanges.push(
                    { id: owningBranchingNode.id, type: 'replace', item: clonedOwningNode },
                    { id: node.id, type: 'remove' },
                )
            } else {
                nodeChanges.push(
                    { id: owningBranchingNode.id, type: 'replace', item: owningBranchingNode },
                    { id: node.id, type: 'remove' },
                )
            }

            // Remove the outgoing edge from the deleted node
            edgeChanges.push({ id: targetNodeEdge.id, type: 'remove' })

            // apply position/meta changes for each node occurring after the removed node
            nodeChanges.push(...applyChangesPostNode('remove', nodes, postNode.id, node.data.step.type))

            return [nodeChanges, edgeChanges]
        }
    }

    // --- Standard linear chain removal ---
    const sourceNodeEdge = edges.find((e) => e.target === node.id && e.type !== 'branch-edge')!
    const targetNodeEdge = edges.find((e) => e.source === node.id)!

    const prevNode = nodes.find((n) => n.id === sourceNodeEdge.source)!
    const postNode = nodes.find((n) => n.id === targetNodeEdge.target)!

    const clonedPrevStep = updateStepConfig(prevNode.data.step, { out_step_id: postNode.data.id })
    const clonedPrevNode = cloneNodeWithStep(prevNode, clonedPrevStep)

    /**
     * update node/edges
     * ```
     *  _________________________________________________________
     * |                                                        |  [action] node will be removed
     * |    current                                             |
     * |        nodes: [trigger]----> [action]  ---->  [exit]   |  [trigger] will be replaced with
     * |        edges:     \_eid-trigger_/ \_eid-action_/       |       [trigger] out_step_id: exit.id
     * |--------------------------------------------------------|
     * |                                                        |  eid-action edge will be removed
     * |    new                                                 |
     * |        nodes: [trigger]  ----->  [exit]                |  eid-trigger will be replaced with
     * |        edges:      \__eid-trigger__/                   |       eid-trigger where target: exit.id
     * |________________________________________________________|
     *
     * ```
     */
    nodeChanges.push(
        {
            id: prevNode.id,
            type: 'replace',
            item: clonedPrevNode,
        },
        {
            id: node.id,
            type: 'remove',
        },
    )

    edgeChanges.push(
        {
            id: sourceNodeEdge.id,
            type: 'replace',
            item: {
                ...sourceNodeEdge,
                source: prevNode.id,
                target: postNode.id,
            },
        },
        {
            id: targetNodeEdge.id,
            type: 'remove',
        },
    )

    // apply position changes for each node occurring after the removed node
    nodeChanges.push(...applyChangesPostNode('remove', nodes, postNode.id, node.data.step.type))

    return [nodeChanges, edgeChanges]
}

export const replacePlaceholderWithLinearStep = (
    convergenceNode: JourneyNode,
    newStepType: LinearAddStepType,
    nodes: JourneyNode[],
): [nodeChanges: NodeChange<JourneyNode>[], edgeChanges: EdgeChange<JourneyEdge>[]] => {
    const outStepId = convergenceNode.data.step.configuration.out_step_id

    // Build new step config preserving out_step_id
    const newStep = getBaseConfigurationForStepType(
        newStepType,
        convergenceNode.id as string,
        outStepId!,
        {},
    )

    // Remove placeholder-specific metadata from uiMeta
    const { convergenceOf, source, placeholderRole, ...restMeta } = convergenceNode.data.uiMeta ?? {}

    // Construct new data without mutating the original node
    const newData = new JourneyStepData({
        step: newStep,
        uiMeta: restMeta,
    })
    const newNode = { ...convergenceNode, data: newData, type: 'custom' as const }

    return [
        [{ type: 'replace', id: convergenceNode.id, item: newNode }],
        [],
    ]
}

export const replacePlaceholderWithBranchingStep = (
    convergenceNode: JourneyNode,
    newStepType: StepType.VARIANT_SPLIT | StepType.CONDITION,
    nodes: JourneyNode[],
    edges: JourneyEdge[],
): [nodeChanges: NodeChange<JourneyNode>[], edgeChanges: EdgeChange<JourneyEdge>[], newConvergence: JourneyNode] => {
    const edgeChanges: EdgeChange<JourneyEdge>[] = []

    const downstreamTargetId = convergenceNode.data.step.configuration.out_step_id
    const branchingNodeId = convergenceNode.id as string

    // 1. Create new convergence node for the nested branch
    const newConvergenceId = getRandId()
    const newConvergenceNode = createPlaceholderConvergenceNode(
        newConvergenceId,
        branchingNodeId,
        downstreamTargetId!,
        { x: convergenceNode.position.x, y: convergenceNode.position.y },
    )

    // 2. Build branching step config (unified for both VARIANT_SPLIT and CONDITION)
    const { step, nodeType, branches } = buildBranchingStepConfig(newStepType, branchingNodeId, newConvergenceId)

    // 3. Clear stale metadata and apply new step
    const { convergenceOf, source, joinTargetOf, placeholderRole, ...restMeta } = convergenceNode.data.uiMeta ?? {}
    const newData = new JourneyStepData({
        step,
        uiMeta: restMeta,
    })
    const newBranchingNode = { ...convergenceNode, data: newData, type: nodeType }

    // 4. Node changes: replace convergence with branching node, add new convergence
    const nodeChanges: NodeChange<JourneyNode>[] = [
        { type: 'replace', id: branchingNodeId, item: newBranchingNode },
        { type: 'add', item: newConvergenceNode },
    ]

    // 5. Edge rewiring
    // Remove old outgoing edge: convergenceNode → downstream target
    const oldOutgoingEdge = edges.find((e) => e.source === branchingNodeId && e.type !== 'branch-edge')
    if (oldOutgoingEdge) {
        edgeChanges.push({ type: 'remove', id: oldOutgoingEdge.id })
    }

    // Add edge: new convergence → downstream target
    edgeChanges.push({
        type: 'add',
        item: {
            ...DEFAULT_CUSTOM_EDGE_CONFIG,
            id: getEdgeId(newConvergenceId),
            source: newConvergenceId,
            target: String(downstreamTargetId),
        },
    })

    // Add split edges: branching node → new convergence (one per branch)
    edgeChanges.push(...createBranchEdges(branchingNodeId, branches))

    return [nodeChanges, edgeChanges, newConvergenceNode]
}

/**
 * Single entry point for replacing any placeholder node with a real step.
 * Handles both linear and branching replacements.
 * Called from:
 *   - ON_PLACEHOLDER_REPLACE reducer action (node click)
 *   - getAddNodeMutations when an adjacent empty placeholder is detected
 */
export const replacePlaceholderWithStep = (
    placeholderNode: JourneyNode,
    newStepType: AddStepType,
    nodes: JourneyNode[],
    edges: JourneyEdge[],
): [
    nodeChanges: NodeChange<JourneyNode>[],
    edgeChanges: EdgeChange<JourneyEdge>[],
    newConvergence?: JourneyNode,
] => {
    const isBranching = isBranchingStepType(newStepType)

    if (isBranching) {
        return replacePlaceholderWithBranchingStep(
            placeholderNode,
            newStepType as StepType.VARIANT_SPLIT | StepType.CONDITION,
            nodes,
            edges,
        )
    }

    const [nc, ec] = replacePlaceholderWithLinearStep(
        placeholderNode,
        newStepType,
        nodes,
    )
    return [nc, ec, undefined]
}



/**
 * Traverses each step -> out_step_id chain and repositions and applies changes for each node from the affected starting
 * node.
 */
export const applyChangesPostNode = (
    effect: 'add' | 'remove',
    nodes: JourneyNode[],
    startingId: string,
    nodeTypeChange?: StepType,
) => {
    const nodeChanges: NodeChange<JourneyNode>[] = []

    let nextStep: string | undefined = startingId
    while (nextStep) {
        const step = nodes.find((n) => n.id === nextStep)
        if (step) {
            if (step.data.step.type === StepType.ACTION && nodeTypeChange === StepType.ACTION) {
                if (!isActionStep(step.data.step)) continue
                const actionStep = step.data.step
                const currentIndex = actionStep.meta?.actionIndex ?? 0
                const newActionIndex = effect === 'add' ? currentIndex + 1 : currentIndex - 1

                const clonedStep = updateStepMeta(actionStep, { actionIndex: newActionIndex })
                const clonedNode = cloneNodeWithStep(step, clonedStep)

                nodeChanges.push({
                    id: step.id,
                    type: 'replace',
                    item: clonedNode,
                })
            }

            if (step.data.step.configuration.out_step_id) {
                nextStep = step.data.step.configuration.out_step_id!.toString()
                continue
            }
        }

        nextStep = undefined
    }

    return nodeChanges
}

// ---- Branching node update mutations (stub — full implementation in Task 2) ----

/**
 * Computes edge changes and orphan cleanup when a branching node's step config changes.
 * Pure function: diffs old branches vs new branches by branchKey to determine
 * removed/updated/added branches, then detects orphaned downstream nodes.
 *
 * This is the immutable replacement for syncBranchingEdgesAndCleanup in reducer.ts.
 * Unlike that function, this one receives clean old/new state and can diff directly.
 *
 * @param oldNode - The branching node before the update (pre-mutation)
 * @param newStep - The new step configuration being applied
 * @param edges - Current edge state
 * @param nodes - Current node state
 * @returns Edge changes to apply and IDs of orphaned nodes to remove
 */
export function getBranchingNodeUpdateMutations(
    oldNode: JourneyNode,
    newStep: IJourneyStep,
    edges: JourneyEdge[],
    nodes: JourneyNode[],
): {
    edgeChanges: EdgeChange<JourneyEdge>[]
    orphanedNodeIds: Set<string>
} {
    const edgeChanges: EdgeChange<JourneyEdge>[] = []
    const orphanedNodeIds = new Set<string>()

    const oldData = getBranchingNodeData(oldNode)
    const newData = getBranchingData(newStep)
    if (!oldData || !newData) return { edgeChanges, orphanedNodeIds }

    const sourceNodeId = String(oldNode.id)
    const joinStepId = newData.join_step_id

    // Build lookup maps
    const oldBranchMap = new Map(oldData.branches.map((b) => [b.key, b]))
    const newBranchMap = new Map(newData.branches.map((b) => [b.key, b]))

    const removedBranchTargets: string[] = []

    // --- Removed branches ---
    for (const [key, oldBranch] of oldBranchMap) {
        if (!newBranchMap.has(key)) {
            const edge = edges.find(
                (e) => e.type === 'branch-edge' && e.source === sourceNodeId
                    && (e.data as BranchEdgeData)?.branchKey === key,
            )
            if (edge) {
                edgeChanges.push({ type: 'remove', id: edge.id })
            }
            if (oldBranch.out_step_id != null) {
                removedBranchTargets.push(String(oldBranch.out_step_id))
            }
        }
    }

    // --- Updated branches ---
    for (const [key, newBranch] of newBranchMap) {
        const oldBranch = oldBranchMap.get(key)
        if (!oldBranch) continue // handled in "added" below

        const existingEdge = edges.find(
            (e) => e.type === 'branch-edge' && e.source === sourceNodeId
                && (e.data as BranchEdgeData)?.branchKey === key,
        )
        if (!existingEdge) continue

        // Detect cleared branch (was pointing somewhere, now reset to join)
        const oldTarget = String(oldBranch.out_step_id)
        const newTarget = newBranch.out_step_id != null ? String(newBranch.out_step_id) : null
        const joinStepIdStr = joinStepId != null ? String(joinStepId) : null
        if (oldTarget !== joinStepIdStr && newTarget === joinStepIdStr) {
            removedBranchTargets.push(oldTarget)
        }

        // Update edge data
        edgeChanges.push({
            type: 'replace',
            id: existingEdge.id,
            item: {
                ...existingEdge,
                target: newTarget ?? existingEdge.target,
                data: {
                    ...(existingEdge.data as BranchEdgeData),
                    branchLabel: newBranch.label,
                    ...(newBranch.kind === 'variant' ? { percentage: newBranch.percentage } : {}),
                },
            },
        })
    }

    // --- Added branches ---
    for (const [key, newBranch] of newBranchMap) {
        if (oldBranchMap.has(key)) continue
        const targetId = newBranch.out_step_id ? String(newBranch.out_step_id) : String(joinStepId)
        const edgeData: BranchEdgeData = {
            branchKey: newBranch.key,
            branchLabel: newBranch.label,
            ...(newBranch.kind === 'variant' ? { percentage: newBranch.percentage } : {}),
        }
        edgeChanges.push({
            type: 'add',
            item: {
                ...DEFAULT_BRANCH_EDGE_CONFIG,
                id: `${getEdgeId(sourceNodeId)}-${newBranch.key}`,
                source: sourceNodeId,
                target: targetId,
                sourceHandle: `branch-${newBranch.key}`,
                data: edgeData,
            } as JourneyEdge,
        })
    }

    // --- Orphan detection ---
    if (removedBranchTargets.length > 0) {
        const allNewOutStepIds = newData.branches.map((b) => b.out_step_id)
        const nodeMap = buildNodeMap(nodes)
        for (const targetId of removedBranchTargets) {
            for (const id of getExclusivelyReachableSteps(
                targetId,
                [...allNewOutStepIds, ...removedBranchTargets],
                joinStepId,
                nodes,
                nodeMap,
            )) {
                orphanedNodeIds.add(id)
            }
        }
    }

    return { edgeChanges, orphanedNodeIds }
}
